import React, { useEffect, useState } from "react";
import { useAuth } from "../../../context/Auth";
import { toast } from "react-toastify";
import ExportData from "../../../components/common/ExportData";
import {
  FileText,
  Calendar,
  Clock,
  DollarSign,
  TrendingUp,
  Users,
  Package,
} from "lucide-react";

const SessionSalesReport = () => {
  const { requestWithAuth } = useAuth();
  const [date, setDate] = useState("");
  const [session, setSession] = useState("Consolidated");
  const [loading, setLoading] = useState(false);
  const [sessionSales, setSessionSales] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [totalSales, setTotalSales] = useState(0.0);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const getSessionSales = async () => {
    if (!date) {
      toast.error("Please select a date");
      return;
    }

    try {
      setLoading(true);
      const data = await requestWithAuth(
        `/report/session-sales/${date}?session=${session}`,
        "GET"
      );
      setSessionSales(data?.branches);
      setTotalSales(data?.total_sales_all_branches || 0);
      setTableData(data?.branches);

      // Calculate totals
      const customers = data?.branches?.reduce(
        (sum, branch) => sum + (parseInt(branch?.customers_served) || 0),
        0
      );
      const items = data?.branches?.reduce(
        (sum, branch) => sum + (parseFloat(branch?.items_sold) || 0),
        0
      );
      setTotalCustomers(customers);
      setTotalItems(parseFloat(items).toFixed(2));
    } catch (error) {
      setSessionSales([]);
      toast.error("Failed to load session sales data");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setDate("");
    setSession("Consolidated");
    setSessionSales([]);
    setTotalSales(0);
    setTotalCustomers(0);
    setTotalItems(0);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Reports</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">
                  Session Sales Report
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <FileText size={32} className="text-blue-600" />
                Session Sales Report
              </h1>
            </div>
          </div>
        </div>

        {/* Summary Cards - Only show when data is loaded */}
        {sessionSales?.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-green-100 rounded-lg">
                  <DollarSign className="text-green-600" size={20} />
                </div>
                <h3 className="text-sm font-medium text-gray-600">
                  Total Sales
                </h3>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                GHS {parseFloat(totalSales || 0).toFixed(2)}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="text-blue-600" size={20} />
                </div>
                <h3 className="text-sm font-medium text-gray-600">
                  Customers Served
                </h3>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {totalCustomers}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Package className="text-purple-600" size={20} />
                </div>
                <h3 className="text-sm font-medium text-gray-600">
                  Items Sold
                </h3>
              </div>
              <p className="text-2xl font-bold text-gray-900">{totalItems}</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Clock className="text-orange-600" size={20} />
                </div>
                <h3 className="text-sm font-medium text-gray-600">Session</h3>
              </div>
              <p className="text-lg font-semibold text-gray-900">
                {session === "Consolidated" ? "All Sessions" : session}
              </p>
            </div>
          </div>
        )}

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Filters Section */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Generate Report
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Session Filter */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  <Clock size={12} className="inline mr-1" />
                  Session
                </label>
                <select
                  value={session}
                  onChange={(e) => setSession(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="Consolidated">All Sessions</option>
                  <option value="morning">Morning Session</option>
                  <option value="evening">Evening Session</option>
                </select>
              </div>

              {/* Date Selector */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  <Calendar size={12} className="inline mr-1" />
                  Date
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                />
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Clear
              </button>
              <button
                onClick={getSessionSales}
                disabled={loading || !date}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${loading || !date
                  ? "bg-blue-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
                  }`}
              >
                {loading ? "Loading..." : "Generate Report"}
              </button>
            </div>
          </div>

          {/* Controls Bar - Only show when data is loaded */}
          {sessionSales?.length > 0 && (
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <ExportData
                    tableData={tableData}
                    fileName={`Session Sales Report - ${date} - ${session}`}
                  />
                </div>

                <div className="text-sm text-gray-600">
                  Date: <span className="font-semibold">{date}</span> | Session:{" "}
                  <span className="font-semibold">
                    {session === "Consolidated" ? "All" : session}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span>Loading session sales data...</span>
                </div>
              </div>
            ) : sessionSales?.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Branch Name
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Customers Served
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Total Sales
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Items Sold
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sessionSales?.map((item, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-4 px-6 text-sm font-medium text-gray-900">
                        {item?.branch}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.customers_served}
                      </td>
                      <td className="py-4 px-6 text-sm font-semibold text-gray-900">
                        GHS {parseFloat(item?.total_sales || 0).toFixed(2)}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.items_sold}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-blue-50 font-bold border-t-2 border-blue-200">
                    <td className="py-4 px-6 text-sm text-gray-900">Total</td>
                    <td className="py-4 px-6 text-sm text-blue-600 font-bold">
                      {totalCustomers}
                    </td>
                    <td className="py-4 px-6 text-sm text-blue-600 font-bold">
                      GHS {parseFloat(totalSales || 0).toFixed(2)}
                    </td>
                    <td className="py-4 px-6 text-sm text-blue-600 font-bold">
                      {totalItems}
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <div className="py-20 text-center">
                <div className="text-gray-400 mb-2">
                  <FileText className="mx-auto h-12 w-12" strokeWidth={1.5} />
                </div>
                <p className="text-gray-500 text-sm font-medium mb-1">
                  No Data Available
                </p>
                <p className="text-gray-400 text-xs">
                  Select a date and session to generate the report
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionSalesReport;
